/**
 * Which shared document is the one on the stage.
 *
 * The Document share panel listed every file identically. With one file that is
 * merely uninformative; with several it is a guessing game, because the only
 * signal anywhere was a "Present" button that looks the same before and after
 * it has been pressed. Someone who shared three documents could not tell which
 * of them the room was looking at.
 *
 * ## Why this is not `presentingDocument.id === doc.id`
 *
 * The id of the presenting document CHANGES shortly after it is chosen. Pressing
 * Present sets it to the document's own id, and the shared-media effect then
 * rewrites it as `remote-<id>` — the same value every other participant
 * receives, since a document on the stage is room state rather than a local
 * selection. Both spellings are live: the raw one for the frames between the
 * click and the effect, and the prefixed one from then on, on every client.
 *
 * A plain equality check is therefore right twice and wrong in between, which
 * shows up as a badge that fails to appear for the person who pressed the
 * button and appears for everybody else — the confusing half of the bug rather
 * than the obvious half.
 */

/** The prefix the shared-media layer stamps on a room-wide item's id. */
const REMOTE_PREFIX = 'remote-'

/**
 * Is `documentId` the document currently on the stage?
 *
 * Compares against both spellings. The prefix is stripped from the PRESENTING
 * id rather than added to the document's, so a document whose own id happens to
 * begin with `remote-` is still matched exactly and never by accident.
 */
export function isDocumentPresenting(
  presentingId: string | null | undefined,
  documentId: string | null | undefined,
): boolean {
  if (!presentingId || !documentId) return false
  if (presentingId === documentId) return true
  return presentingId === `${REMOTE_PREFIX}${documentId}` 
}

/**
 * The document id a presenting id refers to, without the transport's prefix.
 *
 * Exposed for callers that need the identity itself rather than a comparison —
 * a list highlighting one row, say, instead of testing each one.
 */
export function presentingDocumentId(
  presentingId: string | null | undefined,
): string | null {
  if (!presentingId) return null
  return presentingId.startsWith(REMOTE_PREFIX)
    ? presentingId.slice(REMOTE_PREFIX.length) || null
    : presentingId
}
